import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC, RefObject } from "react"

import { useTransferColumns } from "../../hooks/useTransferColumns"
import type { TransferPalletWithProduct } from "../../types"

import { TransferRowDetails } from "./TransferRowDetails"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Transfer } from "@/graphql/codegen/graphql"

interface TransferTableProps {
  data: Transfer[]
  loading?: boolean
  error?: Error
  sorting: SortingState
  expanded: ExpandedState
  isPrinting: boolean
  containerRef?: RefObject<HTMLDivElement>
  onScroll: () => void
  onSortingChange: OnChangeFn<SortingState>
  onExpandedChange: OnChangeFn<ExpandedState>
  onEdit: (transfer: Transfer) => void
  onLpnEdit: (transfer: Transfer) => void
  onLaidDownCostEdit: (transfer: Transfer) => void
  onCancel: (transfer: Transfer) => void
  onReceivedConfirm: (transfer: Transfer) => void
  onWorkOrderConfirm: (transfer: Transfer) => void
  onWorkOrderPrint: (transfer: Transfer) => void
  onPickTicketPrint: (transfer: Transfer) => void
  onPickConfirm: (transfer: Transfer) => void
  onPalletLabelPrint: (pallet: TransferPalletWithProduct) => void
  onPalletLabelPrintAll: (transfer: Transfer) => void
  onDocumentsOpen: (transfer: Transfer) => void
}

export const TransferTable: FC<TransferTableProps> = ({
  data,
  error,
  loading = false,
  sorting,
  expanded,
  isPrinting,
  containerRef,
  onScroll,
  onSortingChange,
  onExpandedChange,
  onEdit,
  onLpnEdit,
  onLaidDownCostEdit,
  onCancel,
  onReceivedConfirm,
  onWorkOrderConfirm,
  onWorkOrderPrint,
  onPickTicketPrint,
  onPickConfirm,
  onPalletLabelPrint,
  onPalletLabelPrintAll,
  onDocumentsOpen,
}) => {
  const columns = useTransferColumns({
    isPrinting,
    onEdit,
    onLpnEdit,
    onLaidDownCostEdit,
    onCancel,
    onReceivedConfirm,
    onWorkOrderConfirm,
    onWorkOrderPrint,
    onPickTicketPrint,
    onPickConfirm,
    onPalletLabelPrintAll,
    onDocumentsOpen,
  })

  return (
    <AerosBaseTable
      error={error}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            expanded,
            isLoading: loading,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          onSortingChange,
          onExpandedChange,
          getRowCanExpand: () => true,
          renderExpandedContent: (row) => (
            <TransferRowDetails row={row} isPrinting={isPrinting} onPalletLabelPrint={onPalletLabelPrint} />
          ),
        },
      }}
      slotProps={{
        container: { ref: containerRef, onScroll },
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      noDataMessage='No transfers found'
    />
  )
}
